<script setup lang="ts">
// Merges legacy OInput + OCustomInput (the "custom" floating-label look — 205 call-sites — is
// the default). Copy-agnostic: label/placeholder/errorMessage are caller-localised props.
import { useId } from 'vue'

import type { InputProps } from './types'

const model = defineModel<string | number>()

withDefaults(defineProps<InputProps>(), {
  type: 'text',
  size: 'md',
  variant: 'default',
  disabled: false,
  readonly: false,
  error: false,
  hideLabel: false,
  autocomplete: 'off',
  monospace: false,
})

const emit = defineEmits<{
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  keydown: [event: KeyboardEvent]
  enter: []
}>()

const inputId = useId()

const inputSizeClasses: Record<NonNullable<InputProps['size']>, string> = {
  xs: 'h-7 text-xs',
  sm: 'h-8 text-xs',
  md: 'h-10 text-xs leading-4',
  lg: 'h-12 text-sm',
  xl: 'h-14 text-sm',
}

// `filled` is the filter-toolbar control: it owns height/type/surface, so it REPLACES the `size`
// step rather than layering on it (no conflicting utility pair is ever emitted).
const fieldVariantClasses: Record<NonNullable<InputProps['variant']>, string> = {
  default: 'rounded-sm bg-surface',
  plain: 'h-10 rounded-sm bg-transparent text-base leading-5 shadow-none',
  filled:
    'h-[2.375rem] rounded-md bg-surface-sunken text-sm leading-5 shadow-none focus:bg-surface',
}
const fieldBorderClasses: Record<NonNullable<InputProps['variant']>, string> = {
  default: 'border-border focus:border-ring focus:shadow-[0_0_0_1px_var(--ui-color-ring)]',
  filled: 'border-transparent focus:border-ring',
  plain: 'border-transparent focus:border-ring',
}
const groupVariantClasses: Record<NonNullable<InputProps['variant']>, string> = {
  default: 'shadow-input',
  plain: '',
  filled: '',
}
</script>

<template>
  <div
    class="ui-input relative w-full font-sans"
    :class="[
      `ui-input--${size}`,
      `ui-input--${variant}`,
      { 'ui-input--error': error, 'ui-input--disabled': disabled },
    ]"
  >
    <label
      v-if="label"
      :for="inputId"
      class="ui-input__label absolute -top-2 left-xs z-raised rounded-sm bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] px-2xs text-xs font-medium text-muted"
      :class="{ 'ui-input__label--sr-only sr-only': hideLabel }"
    >
      {{ label }}
    </label>
    <div
      class="ui-input__group relative flex items-center rounded-sm"
      :class="groupVariantClasses[variant]"
    >
      <span
        v-if="$slots.start"
        class="ui-input__affix ui-input__affix--start absolute left-sm flex items-center text-muted"
      >
        <slot name="start" />
      </span>
      <input
        :id="inputId"
        v-model="model"
        class="ui-input__field w-full border px-sm text-fg outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted"
        :class="[
          fieldVariantClasses[variant],
          variant === 'default' ? inputSizeClasses[size] : '',
          $slots.start ? (variant === 'plain' ? 'pl-10' : '!pl-12') : '',
          $slots.end ? '!pr-10' : '',
          monospace ? 'font-mono' : '',
          error
            ? 'border-danger text-danger focus:shadow-[0_0_0_1px_var(--ui-color-danger)]'
            : fieldBorderClasses[variant],
          { 'cursor-not-allowed opacity-60': disabled },
        ]"
        :type="type"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :step="step"
        :maxlength="maxlength"
        :aria-invalid="error || undefined"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @keydown="emit('keydown', $event)"
        @keydown.enter="emit('enter')"
      />
      <span
        v-if="$slots.end"
        class="ui-input__affix ui-input__affix--end absolute right-sm flex items-center text-muted"
      >
        <slot name="end" />
      </span>
    </div>
    <p
      v-if="error && errorMessage"
      class="ui-input__error mt-2xs text-[0.6875rem] font-medium leading-none text-danger"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
/* Chrome paints an autofilled value in its own system font until the user edits it; the
   `::first-line` pseudo is the only hook it honours. The family is named explicitly because
   `inherit` alone still let the system font through. */
.ui-input__field:-webkit-autofill::first-line {
  font-family: var(--ui-font-sans);
  font-size: inherit;
}
</style>
